import type { LanguagePack, Lesson, Program, Selection, Story, Track, Word } from "../types";

type CatalogPack = Pick<LanguagePack, "definition" | "deck" | "lessons" | "stories" | "programs">;

export interface ContentPart {
  track: Track;
  collection: Track["collections"][number];
  part: Track["collections"][number]["parts"][number];
  deck: Word[]; lessons: Lesson[]; stories: Story[]; programs: Program[];
}

export const catalogTracks = (pack: CatalogPack): Track[] => (pack.definition.tracks ?? []).filter((t) => t.available !== false);

/** Keep only the leading ids of `selection` that exist, so a stale link falls back to the deepest valid level. */
export function validCatalogSelection(pack: CatalogPack, selection: Selection): Selection {
  const track = catalogTracks(pack).find((t) => t.id === selection.trackId);
  if (!track) return {};
  const result: Selection = { trackId: track.id };
  const collection = track.collections?.find((c) => c.id === selection.collectionId && c.available !== false);
  if (!collection) return result;
  result.collectionId = collection.id;
  const part = collection.parts?.find((p) => p.id === selection.partId && p.available !== false);
  if (part) result.partId = part.id;
  return result;
}

export function firstContentPart(pack: CatalogPack): Required<Selection> | null {
  for (const track of catalogTracks(pack)) {
    for (const collection of track.collections ?? []) {
      if (collection.available === false) continue;
      const part = (collection.parts ?? []).find((p) => p.available !== false);
      if (part) return { trackId: track.id, collectionId: collection.id, partId: part.id };
    }
  }
  return null;
}

function pick<T extends { id: string }>(ids: string[] | undefined, items: T[], label: string): T[] {
  const byId = new Map(items.map((x) => [x.id, x]));
  return [...new Set(ids ?? [])].map((id) => {
    const item = byId.get(id);
    if (!item) throw new Error(`Unknown ${label} membership: ${id}`);
    return item;
  });
}

/** The words, lessons, stories and programs that belong to one content part. Throws on dangling references. */
export function resolveContentPart(pack: CatalogPack, selection: Selection): ContentPart | null {
  const track = catalogTracks(pack).find((t) => t.id === selection.trackId);
  const collection = track?.collections?.find((c) => c.id === selection.collectionId && c.available !== false);
  const part = collection?.parts?.find((p) => p.id === selection.partId && p.available !== false);
  if (!track || !collection || !part) return null;
  const deck = pick(part.wordIds, pack.deck, "word");
  const lessons = pick(part.lessonIds, pack.lessons, "lesson");
  const words = new Set(deck.map((w) => w.id));
  if (lessons.some((l) => l.wordIds.some((id) => !words.has(id)))) throw new Error("Lesson words must belong to their content part");
  return {
    track, collection, part, deck, lessons,
    stories: pick(part.storyIds, pack.stories, "story"),
    // Optional audio catalogs may be unavailable; loaded catalogs must have valid membership.
    programs: pack.programs.length ? pick(part.programIds, pack.programs, "program") : [],
  };
}
